// Storage keys
const STORAGE_KEYS = {
  ACCESS_TOKEN: 'accessToken',
  REFRESH_TOKEN: 'refreshToken',
  USER: 'user',
};

/**
 * Set item to localStorage with error handling
 * @param {string} key - Storage key
 * @param {*} value - Value to store
 */
const setItem = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error(`Error saving to localStorage key "${key}":`, error);
  }
};

/**
 * Get item from localStorage with error handling
 * @param {string} key - Storage key
 * @returns {*} Parsed value or null
 */
const getItem = (key) => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : null;
  } catch (error) {
    console.error(`Error reading from localStorage key "${key}":`, error);
    return null;
  }
};

/**
 * Remove item from localStorage
 * @param {string} key - Storage key
 */
const removeItem = (key) => {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error(`Error removing localStorage key "${key}":`, error);
  }
};

/**
 * Clear all auth-related data from localStorage
 */
const clearAuthData = () => {
  try {
    Object.values(STORAGE_KEYS).forEach(key => localStorage.removeItem(key));
  } catch (error) {
    console.error('Error clearing auth data:', error);
  }
};

/**
 * Save auth data to localStorage
 * @param {string} accessToken - JWT access token
 * @param {string} refreshToken - JWT refresh token
 * @param {Object} user - User data object
 */
const saveAuthData = (accessToken, refreshToken, user) => {
  setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
  setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
  setItem(STORAGE_KEYS.USER, user);
};

/**
 * Get auth data from localStorage
 * @returns {Object} Object containing auth data
 */
const getAuthData = () => ({
  accessToken: getItem(STORAGE_KEYS.ACCESS_TOKEN),
  refreshToken: getItem(STORAGE_KEYS.REFRESH_TOKEN),
  user: getItem(STORAGE_KEYS.USER),
});

/**
 * Check if user is authenticated
 * @returns {boolean} True if user has an access token
 */
const isAuthenticated = () => {
  return !!getItem(STORAGE_KEYS.ACCESS_TOKEN);
};

/**
 * Update user data in localStorage
 * @param {Object} userData - Updated user data
 */
const updateUserData = (userData) => {
  try {
    const currentData = getAuthData();
    saveAuthData(
      currentData.accessToken,
      currentData.refreshToken,
      userData
    );
  } catch (error) {
    console.error('Error updating user data:', error);
  }
};

export const storage = {
  setItem,
  getItem,
  removeItem,
  saveAuthData,
  getAuthData,
  clearAuthData,
  isAuthenticated,
  updateUserData,
};
